import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSiteContent } from "../hooks/useSiteContent";

const DEFAULT_LOGO =
  "https://customer-assets.emergentagent.com/job_across-generations/artifacts/6hnqz2w7_GentleGiTo%20Logo.png";

const links = [
  { href: "/journal", label: "Journal" },
  { href: "/#about", label: "About" },
  { href: "/#signup", label: "Subscribe" },
];

export default function Header() {
  const c = useSiteContent();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-background/90 backdrop-blur-md border-b border-border/60 shadow-[0_8px_30px_-20px_rgba(60,55,45,0.18)]"
          : "bg-background/40 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-24 sm:h-28">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-4 group"
          aria-label="GentleGiTo — home"
        >
          <img
            src={c.logo_image || DEFAULT_LOGO}
            alt="GentleGiTo"
            className={`transition-all duration-700 ease-out drop-shadow-[0_2px_6px_rgba(60,55,45,0.06)] flex-shrink-0 ${
              scrolled ? "h-12 sm:h-14" : "h-14 sm:h-16"
            }`}
          />
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-xl sm:text-2xl tracking-tight text-foreground">
              GentleGi<span className="italic text-primary">T</span>o
            </span>
            <span
              data-testid="brand-tagline"
              className="font-serif italic text-[11px] sm:text-xs text-muted-foreground tracking-wide"
            >
              {c.brand_tagline || "Gentle connections across generations"}
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm tracking-wide text-foreground/80 hover:text-primary transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-foreground"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-background/95 backdrop-blur-md border-t border-border/60"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-base text-foreground/80 hover:text-primary"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
